import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useOwnerStore } from '../stores/ownerStore';

const OwnerContext = createContext(null);

export function OwnerProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams(); // Get route params (e.g., ownerSlug from /o/:ownerSlug)
  const initializingRef = useRef(false); // Prevent concurrent initialization
  
  // Zustand store for persistent state
  const {
    currentOwner: storeOwner,
    userOwners: storeUserOwners,
    loading: storeLoading,
    error: storeError,
    setCurrentOwner: setStoreOwner,
    setUserOwners: setStoreUserOwners,
    setLoading: setStoreLoading,
    setError: setStoreError,
    clearOwner,
    getLastAccessedOwner,
    findOwnerBySlug,
  } = useOwnerStore();

  // Local state (can override store for reactive updates)
  const [currentOwner, setCurrentOwner] = useState(storeOwner);
  const [userOwners, setUserOwners] = useState(storeUserOwners);
  const [loading, setLoading] = useState(storeLoading);
  const [error, setError] = useState(storeError);

  // Sync local state with store
  useEffect(() => {
    setCurrentOwner(storeOwner);
    setUserOwners(storeUserOwners);
    setLoading(storeLoading);
    setError(storeError);
  }, [storeOwner, storeUserOwners, storeLoading, storeError]);

  // Resolve owner slug from URL or (in limited cases) subdomain
  const resolveOwnerSlug = () => {
    const hostname = window.location.hostname || '';
    const pathname = location.pathname || '';

    // Strategy A: Explicit route parameter (/o/:ownerSlug) - highest priority
    if (params.ownerSlug) {
      return params.ownerSlug;
    }

    // Strategy B: Path-based slug matching (/o/:ownerSlug/...)
    const pathMatch = pathname.match(/^\/o\/([^/]+)/);
    if (pathMatch && pathMatch[1]) {
      return pathMatch[1];
    }

    // Strategy C: Subdomain-based slug for real tenant domains only
    // Ignore preview hashes and localhost/pages.dev/main domain
    const isPreview = hostname.endsWith('pages.dev') || hostname === 'localhost';
    const isMainDomain = hostname === 'rentaloop.ae' || hostname === 'www.rentaloop.ae';

    if (!isPreview && !isMainDomain) {
      const parts = hostname.split('.');
      if (parts.length > 1) {
        const subdomain = parts[0];
        if (subdomain && subdomain !== 'www' && subdomain !== 'rentaloop') {
          return subdomain;
        }
      }
    }

    // No valid slug found
    return null;
  };

  // Fetch owner by slug
  const fetchOwnerBySlug = async (slug) => {
    if (!slug || typeof slug !== 'string' || slug.trim() === '') {
      throw new Error('Invalid slug provided');
    }

    const { data, error } = await supabase
      .from('owners')
      .select('id, name, slug')
      .eq('slug', slug.trim())
      .single();

    if (error) {
      // Handle 406 (Not Acceptable) - usually RLS blocking or malformed query
      if (error.status === 406 || error.code === 'PGRST116') {
        console.warn('RLS policy blocked owner query or query format issue:', {
          slug,
          status: error.status,
          code: error.code
        });
        throw new Error(`Access denied or owner not found: ${slug}`);
      }
      throw new Error(`Owner not found: ${slug} - ${error.message}`);
    }

    if (!data) {
      throw new Error(`Owner not found: ${slug}`);
    }

    return data;
  };

  // Verify user has role at owner
  // Returns the role if user has access (admin, owner, manager, viewer), null otherwise
  const verifyUserRole = async (ownerId) => {
    if (!user) return null;

    // Validate ownerId is not a placeholder
    const placeholderUUID = '11111111-1111-1111-1111-111111111111';
    if (!ownerId || ownerId === placeholderUUID) {
      console.warn('Invalid or placeholder ownerId in verifyUserRole:', ownerId);
      return null;
    }

    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('owner_id', ownerId)
      .eq('user_id', user.id)
      .single();

    if (error) {
      if (error.status === 406 || error.code === 'PGRST116') {
        console.warn('RLS policy blocked user_roles query');
      }
      return null;
    }

    if (!data) {
      return null;
    }

    // Accept any role that grants access: admin, owner, manager, viewer
    // All of these should have access to view/manage the owner's data
    const allowedRoles = ['admin', 'owner', 'manager', 'viewer'];
    if (allowedRoles.includes(data.role)) {
      return data.role;
    }

    console.warn('[OwnerContext] User has role but it is not in allowedRoles:', data.role);
    return null;
  };

  // Fetch all owners user has access to
  // FIXED: Only uses user_roles table, no user_profiles dependency
  // GUARD: Early return if no user
  const fetchUserOwners = async () => {
    if (!user?.id) {
      console.log('[OwnerContext] No user ID, returning empty owners');
      return [];
    }

    console.log('[OwnerContext] 🔍 Starting owner resolution for user:', user.id);
    
    // Query user_roles - use maybeSingle() equivalent (no .single() to allow 0 rows)
    let rolesData, rolesError;
    try {
      const result = await supabase
        .from('user_roles')
        .select('owner_id, role, owners(id, name, slug)')
        .eq('user_id', user.id);
      rolesData = result.data;
      rolesError = result.error;
    } catch (err) {
      console.error('[OwnerContext] Exception during user_roles query:', err);
      rolesError = {
        message: err.message || 'Unknown error',
        status: 500,
        code: 'UNKNOWN_ERROR'
      };
      rolesData = null;
    }

    // GUARD: Handle errors - set error and STOP
    if (rolesError) {
      console.error('[OwnerContext] ❌ ERROR fetching user_roles:', {
        userId: user.id,
        error: rolesError,
        code: rolesError.code,
        message: rolesError.message,
        status: rolesError.status,
        details: rolesError.details,
        hint: rolesError.hint,
      });
      
      // Return empty array - caller will handle error state
        return [];
      }
      
    // GUARD: If no roles found, return empty array (caller will set error)
    if (!rolesData || rolesData.length === 0) {
      console.log('[OwnerContext] No user_roles entries found for user');
      return [];
    }

    console.log('[OwnerContext] ✅ Found user_roles entries:', rolesData.length);
    
      const validOwners = [];
      
      for (const item of rolesData) {
      // Try to get owner from join first
        if (item.owners && typeof item.owners === 'object' && item.owners.id) {
        if (item.owners.id && item.owners.name) {
          // Generate slug if missing
          let ownerSlug = item.owners.slug;
          if (!ownerSlug || !ownerSlug.trim()) {
            ownerSlug = item.owners.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          }
          
            validOwners.push({
              id: item.owners.id,
              name: item.owners.name,
            slug: ownerSlug || item.owners.id, // Use ID as fallback if no slug
              role: item.role,
            });
            continue;
        }
      }
      
      // Fallback: fetch owner directly if join failed
        // GUARD: Only fetch if owner_id exists
        if (item.owner_id) {
          try {
            // Use maybeSingle() instead of single() to handle 0 rows gracefully
            const { data: owner, error: ownerError } = await supabase
              .from('owners')
              .select('id, name, slug')
              .eq('id', item.owner_id)
              .maybeSingle();
            
          if (!ownerError && owner && owner.id && owner.name) {
            let ownerSlug = owner.slug;
            if (!ownerSlug || !ownerSlug.trim()) {
              ownerSlug = owner.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            }
            
              validOwners.push({
                id: owner.id,
                name: owner.name,
              slug: ownerSlug || owner.id,
                role: item.role,
              });
            } else if (ownerError) {
              console.warn('[OwnerContext] Failed to fetch owner directly:', ownerError);
            }
          } catch (err) {
            console.warn('[OwnerContext] Error fetching owner directly:', err);
          }
        }
      }
      
      if (validOwners.length > 0) {
      console.log('[OwnerContext] ✅ Successfully resolved owners:', {
          count: validOwners.length,
          owners: validOwners.map(o => ({ id: o.id, name: o.name, slug: o.slug, role: o.role })),
        });
        return validOwners;
    }
    
    console.log('[OwnerContext] No valid owners found after processing user_roles');
    return [];
  };

  // Navigate to owner (update URL with /o/:slug pattern)
  const navigateToOwner = (owner) => {
    // Validate owner object has all required fields
    if (!owner || typeof owner !== 'object') {
      console.warn('Invalid owner object passed to navigateToOwner:', owner);
      return;
    }
    
    if (!owner.id || !owner.name || !owner.slug) {
      console.warn('Owner missing required fields (id, name, or slug):', {
        hasId: !!owner.id,
        hasName: !!owner.name,
        hasSlug: !!owner.slug,
        ownerId: owner?.id,
        ownerName: owner?.name
      });
      return;
    }
    
    if (!owner.slug.trim()) {
      console.warn('Owner has empty slug:', {
        ownerId: owner.id,
        ownerName: owner.name
      });
      return;
    }

    // Update both local state and store
    setCurrentOwner(owner);
    setStoreOwner(owner);
    
    // Update URL if not already correct
    const expectedPath = `/o/${owner.slug}`;
    const currentPath = location.pathname;
    
    // Don't add owner prefix to login routes (they should remain public)
    const publicRoutes = ['/admin-login', '/tenant-login', '/tenant/register', '/select-owner'];
    if (publicRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'))) {
      // If on a public route, just navigate to dashboard
      navigate(`${expectedPath}/admin-dashboard`);
      return;
    }
    
    // Check if we're already on this owner's path
    if (!currentPath.startsWith(expectedPath)) {
      // Preserve rest of path after /o/:slug, or default to dashboard
      let restOfPath = currentPath.replace(/^\/o\/[^/]+/, '');
      // Also handle paths without /o/:slug prefix
      if (!currentPath.startsWith('/o/')) {
        // Extract path after /admin- or /tenant- prefixes
        restOfPath = currentPath.replace(/^\/(admin-|tenant-)/, '/admin-') || '/admin-dashboard';
      }
      if (!restOfPath || restOfPath === '/') {
        restOfPath = '/admin-dashboard';
      }
      navigate(`${expectedPath}${restOfPath}`);
    }
  };

  // Switch owner
  const switchOwner = (owner) => {
    navigateToOwner(owner);
  };

  // Initialize owner context
  useEffect(() => {
    const initializeOwner = async () => {
      if (!user) {
        setLoading(false);
        setStoreLoading(false);
        initializingRef.current = false;
        return;
      }

      // Prevent re-initialization if already initializing (avoid infinite loops)
      if (initializingRef.current) {
        console.log('[OwnerContext] Already initializing, skipping duplicate call');
        return;
      }

      initializingRef.current = true;

      // Timeout safeguard: if initialization takes more than 30 seconds, force completion
      let timeoutId;
      try {
        timeoutId = setTimeout(() => {
          if (initializingRef.current) {
            console.warn('[OwnerContext] Initialization timeout - forcing completion');
            setLoading(false);
            setStoreLoading(false);
            setError('Owner initialization timed out. Please refresh the page.');
            setStoreError('Owner initialization timed out. Please refresh the page.');
            initializingRef.current = false;
          }
        }, 30000); // 30 second timeout
        setLoading(true);
        setStoreLoading(true);
        setError(null);
        setStoreError(null);

        // Fetch all owners user has access to
        // Store error state to check for 500 errors
        let has500Error = false;
        let last500Error = null;
        
        const owners = await fetchUserOwners();
        setUserOwners(owners);
        setStoreUserOwners(owners);

        // Handle 0 owners: Show onboarding UI
        if (owners.length === 0) {
          console.log('[OwnerContext] No owners found - user needs onboarding');
          // Don't set error - let UI show onboarding state
          setError(null);
          setStoreError(null);
          setLoading(false);
          setStoreLoading(false);
          initializingRef.current = false;
          return;
        }

        // Handle 1 owner: Auto-select it
        if (owners.length === 1) {
          const owner = owners[0];
            const role = await verifyUserRole(owner.id);
          if (role) {
            const ownerWithRole = { ...owner, role };
            setCurrentOwner(ownerWithRole);
            setStoreOwner(ownerWithRole);
            navigateToOwner(ownerWithRole);
            setLoading(false);
            setStoreLoading(false);
            initializingRef.current = false;
            return;
            } else {
            console.warn('[OwnerContext] User has owner but no valid role');
            setError('You do not have a valid role for this owner. Please contact your administrator.');
            setStoreError('You do not have a valid role for this owner. Please contact your administrator.');
            setLoading(false);
            setStoreLoading(false);
            initializingRef.current = false;
            return;
          }
        }

        // Handle multiple owners: Check localStorage for last selected, or show modal
        if (owners.length > 1) {
          console.log('[OwnerContext] Multiple owners found:', owners.length);
          
          // Check localStorage for last selected owner
          const lastSelectedOwnerId = localStorage.getItem('lastSelectedOwnerId');
          if (lastSelectedOwnerId) {
            const lastOwner = owners.find(o => o.id === lastSelectedOwnerId);
            if (lastOwner) {
              const role = await verifyUserRole(lastOwner.id);
              if (role) {
                const ownerWithRole = { ...lastOwner, role };
                setCurrentOwner(ownerWithRole);
                setStoreOwner(ownerWithRole);
                navigateToOwner(ownerWithRole);
                setLoading(false);
                setStoreLoading(false);
                initializingRef.current = false;
                return;
              }
            }
          }
          
          // No last selected or invalid - check URL slug
          const slug = resolveOwnerSlug();
          if (slug) {
            const ownerBySlug = owners.find(o => o.slug === slug);
            if (ownerBySlug) {
              const role = await verifyUserRole(ownerBySlug.id);
              if (role) {
                const ownerWithRole = { ...ownerBySlug, role };
                setCurrentOwner(ownerWithRole);
                setStoreOwner(ownerWithRole);
                // Store in localStorage
                localStorage.setItem('lastSelectedOwnerId', ownerWithRole.id);
                setLoading(false);
                setStoreLoading(false);
                initializingRef.current = false;
                return;
              }
            }
          }
          
          // No valid selection - use first owner as default
          const firstOwner = owners[0];
          const role = await verifyUserRole(firstOwner.id);
          if (role) {
            const ownerWithRole = { ...firstOwner, role };
            setCurrentOwner(ownerWithRole);
            setStoreOwner(ownerWithRole);
            localStorage.setItem('lastSelectedOwnerId', ownerWithRole.id);
            navigateToOwner(ownerWithRole);
            setLoading(false);
            setStoreLoading(false);
            initializingRef.current = false;
            return;
          } else {
            console.warn('[OwnerContext] User has owner but no valid role');
            setError('You do not have a valid role for this owner. Please contact your administrator.');
            setStoreError('You do not have a valid role for this owner. Please contact your administrator.');
            setLoading(false);
            setStoreLoading(false);
            initializingRef.current = false;
            return;
          }
        }
      } catch (err) {
        console.error('Error initializing owner:', err);
        const errorMsg = err.message || 'Failed to initialize owner';
        setError(errorMsg);
        setStoreError(errorMsg);
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId); // Clear timeout if we completed normally
        }
        setLoading(false);
        setStoreLoading(false);
        initializingRef.current = false;
      }
    };

    initializeOwner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, params.ownerSlug]); // Re-initialize only if user ID or ownerSlug changes (not user object reference)

  // Get current owner ID (for queries)
  const getCurrentOwnerId = () => {
    return currentOwner?.id || null;
  };

  // Get current owner slug
  const getCurrentOwnerSlug = () => {
    return currentOwner?.slug || null;
  };

  const value = {
    currentOwner,
    userOwners,
    loading,
    error,
    switchOwner,
    getCurrentOwnerId,
    getCurrentOwnerSlug,
    refresh: async () => {
      // Force re-initialization
      setLoading(true);
      setStoreLoading(true);
      try {
        const owners = await fetchUserOwners();
        setUserOwners(owners);
        setStoreUserOwners(owners);
        if (currentOwner) {
          const updated = owners.find(o => o.id === currentOwner.id);
          if (updated) {
            const ownerWithRole = { ...updated, role: updated.role };
            setCurrentOwner(ownerWithRole);
            setStoreOwner(ownerWithRole);
          }
        }
      } catch (err) {
        console.error('Error refreshing owners:', err);
        setError(err.message);
        setStoreError(err.message);
      } finally {
        setLoading(false);
        setStoreLoading(false);
      }
    },
    clearOwner: () => {
      clearOwner();
      setCurrentOwner(null);
    },
    initializeOwner: async () => {
      // Re-initialize owner context (public method for external use)
      if (initializingRef.current) {
        console.log('[OwnerContext] Already initializing, skipping...');
        return;
      }
      initializingRef.current = true;
      setLoading(true);
      setStoreLoading(true);
      setError(null);
      setStoreError(null);
      
      try {
        const owners = await fetchUserOwners();
        setUserOwners(owners);
        setStoreUserOwners(owners);
        
        if (owners.length > 0) {
          // Use first owner or last accessed
          const lastOwner = getLastAccessedOwner();
          const ownerToUse = lastOwner && owners.find(o => o.id === lastOwner.id && o.slug && o.slug.trim())
            ? lastOwner
            : owners.find(o => o.slug && o.slug.trim()) || owners[0];
          
          if (ownerToUse) {
            const role = await verifyUserRole(ownerToUse.id);
            const ownerWithRole = { ...ownerToUse, role: role || ownerToUse.role };
            setCurrentOwner(ownerWithRole);
            setStoreOwner(ownerWithRole);
            navigateToOwner(ownerWithRole);
          }
        }
      } catch (err) {
        console.error('[OwnerContext] Error re-initializing owner:', err);
        setError(err.message);
        setStoreError(err.message);
      } finally {
        setLoading(false);
        setStoreLoading(false);
        initializingRef.current = false;
      }
    },
  };

  return (
    <OwnerContext.Provider value={value}>
      {children}
    </OwnerContext.Provider>
  );
}

export function useOwner() {
  const context = useContext(OwnerContext);
  if (!context) {
    throw new Error('useOwner must be used within OwnerProvider');
  }
  return context;
}


